import type { MonitorandprotectCampaignsListData } from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { apiClient } from "app/modules/api-client/apiClient";
import queryClient from "single-spa/react-app/query-client";

import { ROUTES } from "../../../app/keywords/constants";
import { MONITOR_AND_PROTECT_GET_CAMPAIGNS_KEY } from "../../../services/api/campaign";
import type { RedirectHandler } from "../types";

export const homepageRedirectHandler: RedirectHandler = async (transition) => {
    const toState = transition.to();

    // Don't redirect if already going to the home state
    if (toState.name === ROUTES.HOME.stateName) {
        return;
    }

    try {
        // First, try to get cached data
        let campaignsList = queryClient.getQueryData<Array<MonitorandprotectCampaignsListData>>(
            MONITOR_AND_PROTECT_GET_CAMPAIGNS_KEY,
        );

        const isCacheInvalid = !Array.isArray(campaignsList) || campaignsList.length === 0;
        if (isCacheInvalid) {
            // If no cache, fetch and cache the data
            campaignsList = await queryClient.fetchQuery({
                queryKey: MONITOR_AND_PROTECT_GET_CAMPAIGNS_KEY,
                queryFn: async () => {
                    const response = await apiClient.MonitorAndProtectMaster.monitorandprotectCampaignsList({});
                    return response ?? [];
                },
                staleTime: Infinity,
            });
        }

        // If no campaigns exist, redirect to monitor-and-protect home page
        if (campaignsList.length === 0) {
            return ROUTES.HOME.stateName;
        }

        // If campaigns exist, allow the transition to proceed
        return;
    } catch (error) {
        const toParams = transition.params();
        const toState = transition.to();

        Sentry.errorTracker({
            e: error,
            message: "Error checking campaigns in monitor-and-protect redirect",
            level: "error",
            params: {
                extra: {
                    toParams,
                    toState: toState.name,
                },
            },
        });

        // On error redirect to home state, allowing the user to return to a safe state
        return ROUTES.HOME.stateName;
    }
};
